import type { ReactNode } from 'react'

/**
 * Logique pure des barres d'onglets (`AppTabs`), sans React ni registre de routes : partagée par
 * Logistique et Suivi de projet, testée par `logistics-tabs-state.test.mjs` et
 * `product-management-tabs.test.mjs`.
 */

interface TabDefinition<TValue extends string> {
  value: TValue
  /** Onglet annoncé mais pas encore livré : toujours désactivé, jamais sélectionné. */
  comingSoon?: boolean
}

interface TabAuthorization {
  authorized: boolean
  tooltip?: ReactNode
}

/**
 * Onglets prêts pour `AppTabs` (un onglet interdit reste affiché, désactivé avec son tooltip) et
 * onglet actif : celui de l'URL s'il est ouvert, sinon le premier onglet ouvert.
 * `authorizations[i]` est la résolution des droits de `tabs[i]` (`useAuthorizedTabs`).
 */
export const resolveTabs = <TValue extends string, TTab extends TabDefinition<TValue>>(
  tabs: readonly TTab[],
  authorizations: readonly TabAuthorization[],
  requestedTab: TValue,
  comingSoonTooltip: ReactNode = 'Bientôt',
): { tabs: (TTab & { disabled: boolean; tooltip?: ReactNode })[]; activeTab: TValue } => {
  const resolved = tabs.map((tab, index) =>
    tab.comingSoon
      ? { ...tab, disabled: true, tooltip: comingSoonTooltip }
      : {
          ...tab,
          disabled: !authorizations[index]?.authorized,
          tooltip: authorizations[index]?.tooltip,
        },
  )
  const isOpen = (value: TValue) => resolved.some((tab) => tab.value === value && !tab.disabled)
  const activeTab = isOpen(requestedTab)
    ? requestedTab
    : (resolved.find((tab) => !tab.disabled)?.value ?? requestedTab)
  return { tabs: resolved, activeTab }
}
